import { ChatHistoryRecord } from "@/types/history";
import { ChatEntry, Suggestion } from "@/types/chat";
import { plainUserText } from "@/lib/plainUserText";
import { isPrimaryChatEntry } from "@/config/chat.config";
import { ChatMessage } from "./TextAgentPage";

export const convertHistoryToMessages = (history: ChatHistoryRecord[]): ChatMessage[] => {
  return history.map((item) => ({
    id: item.id.toString(),
    role: item.message.role,
    content:
      item.message.role === "user"
        ? plainUserText(item.message.content)
        : item.message.content,
    timestamp: new Date(item.message.timestamp),
  }));
};

export interface ResolveMessagesFromHistoryParams {
  chatHistory: ChatHistoryRecord[] | undefined;
  localMessages: ChatMessage[];
  isSendingMessage: boolean;
  chatEntry: ChatEntry;
}

export interface ResolveMessagesFromHistoryResult {
  /** Whether `messages`/`suggestions` should replace current local state. */
  applied: boolean;
  messages: ChatMessage[];
  suggestions: Suggestion[];
}

/**
 * Decides whether a freshly-queried chat history should replace the live local
 * transcript. Guards the AE-192 race: right after a stream's `done` event
 * flips `isCurrentSessionNew` false, the history query re-enables while the
 * edge function's persist() may still be writing rows, so history can
 * momentarily come back empty or partial. Treating that as authoritative
 * would wipe the just-streamed assistant bubble and clear suggestions.
 */
export const resolveMessagesFromHistory = ({
  chatHistory,
  localMessages,
  isSendingMessage,
  chatEntry,
}: ResolveMessagesFromHistoryParams): ResolveMessagesFromHistoryResult => {
  const keepLocal: ResolveMessagesFromHistoryResult = {
    applied: false,
    messages: localMessages,
    suggestions: [],
  };

  if (chatHistory === undefined || isSendingMessage) {
    return keepLocal;
  }
  if (chatHistory.length === 0 && localMessages.length > 0) {
    return keepLocal;
  }

  const historyMessages = convertHistoryToMessages(chatHistory);
  if (!isPrimaryChatEntry(chatEntry)) {
    historyMessages.shift();
  }

  if (localMessages.length > 0 && historyMessages.length < localMessages.length) {
    return keepLocal;
  }

  return {
    applied: true,
    messages: historyMessages,
    suggestions: chatHistory[chatHistory.length - 1]?.message?.suggestions || [],
  };
};
